import React, { useEffect, useRef, useState } from "react";

// Cursor sparkle trail. Two modes:
//   - Global (default): follows the cursor across the whole viewport (position: fixed)
//   - Scoped: pass a containerRef to constrain sparkles to that element
// Zero deps, canvas-driven for perf.
export default function CursorSparkles({ containerRef, global = false, colors = ["#FFF7EA", "#A7C7E7", "#FFD6E7", "#F7F9FC", "#7B9ACC"] }) {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = () => setReduced(mq.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  useEffect(() => {
    const isGlobal = global || !containerRef;
    const c = isGlobal ? null : containerRef?.current;
    const cvs = canvasRef.current;
    if (!cvs || reduced) return;
    if (!isGlobal && !c) return;

    // Skip on touch-only devices — no cursor to trail
    const hasFinePointer = window.matchMedia?.("(pointer: fine)").matches;
    if (!hasFinePointer) return;

    const ctx = cvs.getContext("2d");
    const dpr = Math.max(1, window.devicePixelRatio || 1);

    const resize = () => {
      const rw = isGlobal ? window.innerWidth : c.getBoundingClientRect().width;
      const rh = isGlobal ? window.innerHeight : c.getBoundingClientRect().height;
      cvs.width = Math.floor(rw * dpr);
      cvs.height = Math.floor(rh * dpr);
      cvs.style.width = `${rw}px`;
      cvs.style.height = `${rh}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    let ro = null;
    if (isGlobal) {
      window.addEventListener("resize", resize);
    } else {
      ro = new ResizeObserver(resize);
      ro.observe(c);
    }

    let lastSpawn = 0;
    let lastX = 0, lastY = 0;
    const spawn = (x, y) => {
      const count = 2 + Math.floor(Math.random() * 3);
      for (let i = 0; i < count; i++) {
        particlesRef.current.push({
          x: x + (Math.random() - 0.5) * 8,
          y: y + (Math.random() - 0.5) * 8,
          vx: (Math.random() - 0.5) * 0.7,
          vy: -0.4 - Math.random() * 0.7,
          rot: Math.random() * Math.PI * 2,
          vr: (Math.random() - 0.5) * 0.09,
          size: 3 + Math.random() * 6,
          life: 0,
          maxLife: 800 + Math.random() * 600,
          color: colors[(Math.random() * colors.length) | 0],
        });
      }
      if (particlesRef.current.length > 260) particlesRef.current.splice(0, particlesRef.current.length - 260);
    };

    const onMove = (e) => {
      let x, y;
      if (isGlobal) {
        x = e.clientX; y = e.clientY;
      } else {
        const r = c.getBoundingClientRect();
        x = e.clientX - r.left;
        y = e.clientY - r.top;
        if (x < 0 || y < 0 || x > r.width || y > r.height) return;
      }
      const now = performance.now();
      const dx = x - lastX, dy = y - lastY;
      const dist = Math.hypot(dx, dy);
      if (now - lastSpawn > 20 && dist > 3) {
        spawn(x, y);
        lastSpawn = now;
      }
      lastX = x; lastY = y;
    };
    const target = isGlobal ? window : c;
    target.addEventListener("mousemove", onMove, { passive: true });

    const drawStar = (x, y, size, rot, alpha, color) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      // 4-point sparkle
      ctx.beginPath();
      ctx.moveTo(0, -size);
      ctx.quadraticCurveTo(0, 0, size, 0);
      ctx.quadraticCurveTo(0, 0, 0, size);
      ctx.quadraticCurveTo(0, 0, -size, 0);
      ctx.quadraticCurveTo(0, 0, 0, -size);
      ctx.closePath();
      ctx.fill();
      // subtle glow
      ctx.globalAlpha = alpha * 0.35;
      ctx.beginPath();
      ctx.arc(0, 0, size * 0.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    let prev = performance.now();
    const loop = (now) => {
      const dt = now - prev;
      prev = now;
      ctx.clearRect(0, 0, cvs.width, cvs.height);
      const list = particlesRef.current;
      for (let i = list.length - 1; i >= 0; i--) {
        const p = list[i];
        p.life += dt;
        if (p.life > p.maxLife) { list.splice(i, 1); continue; }
        p.x += p.vx; p.y += p.vy; p.rot += p.vr;
        p.vy -= 0.008; // slight lift
        const t = p.life / p.maxLife;
        // grow briefly then shrink
        const grow = t < 0.25 ? t / 0.25 : 1 - (t - 0.25) / 0.75;
        const size = p.size * Math.max(0, grow);
        const alpha = Math.max(0, 1 - t);
        drawStar(p.x, p.y, size, p.rot, alpha, p.color);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      target.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(rafRef.current);
      if (ro) ro.disconnect();
      if (isGlobal) window.removeEventListener("resize", resize);
      particlesRef.current = [];
    };
  }, [containerRef, colors, reduced, global]);

  const isGlobal = global || !containerRef;
  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={`${isGlobal ? "fixed" : "absolute"} inset-0 pointer-events-none ${isGlobal ? "z-[9999]" : "z-[5]"}`}
      style={{ mixBlendMode: "screen" }}
      data-testid="cursor-sparkles"
    />
  );
}
